<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">

    <v-container fluid class="pa-0">
        <v-row no-gutters>
            <!-- INSERT READY TO GO MESSAGE -->
            <v-col sm="12" v-if="config.action==='insert' && isUploadPossible">
                <v-alert type="info" class="body-2">
                    Ok captain. We are ready to go! Click on the upload button to start.
                </v-alert>
            </v-col>

            <!-- INSERT REQUIRED FIELDS MISSING ERROR-->
            <v-col sm="12" v-if="config.action==='insert' && createRequiredFieldsMissing.length > 0">
                <v-alert type="error" class="body-2">
                    The following required fields are missing {{ createRequiredFieldsMissing.join(', ') }}. This import
                    is not possible.
                </v-alert>

                <v-btn color="primary" @click="backClicked">
                    Go back to mapping
                </v-btn>

                <v-btn color="secondary" @click="cancelClicked">
                    Cancel
                </v-btn>
            </v-col>

            <!-- INSERT WARNING IF FIELDS ARE MISSING -->
            <v-col sm="12"
                   v-if="!createErrorsFound && config.action==='insert' && createRecommendedFieldsMissing.length > 0 && !isUploadPossible">
                <v-alert type="warning" class="body-2">
                    Your mapping doesn't include the following recommended variables<br>
                    <v-chip color="grey darken-1" v-for="field in createRecommendedFieldsMissing" :key="field.name">
                        {{ field.name }}
                    </v-chip>
                    <br>
                    If you continue this import you must select fixed value to be inserted for each panelists.
                    If this is not what you want please add suitable columns into your CSV import file
                    for these variables.
                </v-alert>
            </v-col>

            <v-col sm="12" v-if="config.action==='update'">
                <v-card>
                    <v-card-title>Update settings</v-card-title>
                    <v-row>
                        <v-col sm="12">
                            <v-alert type="error" :value="!updateKeysFound">
                                No update keys found in your CSV file. Update keys supported are EMAIL and
                                IDENTIFIER
                            </v-alert>
                        </v-col>

                        <v-col sm="6" class="pa-2">

                            <v-select :items="updateKeys"
                                      v-model="updateKeyModel"
                                      item-value="id"
                                      item-text="name"
                                      return-object
                                      label="Select update key"
                            ></v-select>

                        </v-col>

                    </v-row>

                </v-card>
            </v-col>

            <!-- CREATE PANELIST OPTIONS -->
            <v-col sm="12"
                   v-if="config.action==='insert' && !createErrorsFound && createRecommendedFieldsMissing.length>0">

                <v-container fluid class="pa-0">

                    <v-row>
                        <v-col sm="12" md="6" v-if="createRecommendedFieldsMissing.length>0">

                            <title-card title="Create options" help="panelist-importer-create-options">

                                <!-- SUB PANEL -->
                                <v-select
                                    :items="subPanels"
                                    v-model="config.subPanelUuid"
                                    item-text="name"
                                    item-value="uuid"
                                    label="Target sub panel"
                                    prepend-icon="mdi-sitemap"
                                ></v-select>

                                <!-- LOCALES -->
                                <v-select
                                    v-show="config.subPanelUuid"
                                    :items="locales"
                                    v-model="config.localeId"
                                    item-text="code"
                                    item-value="id"
                                    label="Assign locale (when missing)"
                                    prepend-icon="mdi-earth"
                                ></v-select>

                                <!-- RECRUITMENT SOURCE SELECTION -->
                                <v-select
                                    :items="enabledRecruitmentSources"
                                    v-model="config.recruitmentSourceId"
                                    item-text="name"
                                    item-value="id"
                                    label="Assign recruitment source (when missing)"
                                    prepend-icon="mdi-application-import"
                                ></v-select>

                                <!-- DATE PICKER -->
                                <v-dialog
                                    ref="dialog"
                                    v-model="datePickerModel"
                                    :return-value.sync="config.subscribedDate"
                                    persistent
                                    width="500px"
                                >
                                    <template v-slot:activator="{ on }">
                                        <v-text-field
                                            v-model="config.subscribedDate"
                                            label="Assign join date (when missing)"
                                            prepend-icon="mdi-calendar-question"
                                            readonly
                                            v-on="on"
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker
                                        v-model="config.subscribedDate"
                                        scrollable
                                        landscape
                                        color="primary"
                                        header-color="primary"
                                        :max="today"
                                    >
                                        <v-spacer></v-spacer>
                                        <v-btn color="secondary" @click="datePickerModel = false">Cancel</v-btn>
                                        <v-btn color="primary" @click="$refs.dialog.save(config.subscribedDate)">
                                            OK
                                        </v-btn>
                                    </v-date-picker>
                                </v-dialog>

                            </title-card>
                        </v-col>

                        <!--- IMPORT OPTIONS -->

                        <v-col sm="12" md="6">
                            <title-card title="Create options" help="panelist-importer-import-options">

                                <v-select
                                    :items="errorHandlingModes"
                                    v-model="config.errorHandling"
                                    item-text="name"
                                    item-value="mode"
                                    label="Error handling"
                                    prepend-icon="mdi-alert-circle-outline"
                                ></v-select>

                            </title-card>

                        </v-col>
                    </v-row>

                </v-container>

                <v-col sm="12">
                    <v-btn :disabled="!isUploadPossible" color="primary" :loading="importing" @click="uploadFile">
                        Import
                        <v-icon right dark>mdi-cloud-upload</v-icon>
                    </v-btn>

                    <v-btn color="secondary" @click="cancelClicked">
                        Restart
                    </v-btn>

                </v-col>

                <v-col sm="12" v-if="debug">
                    <json :data="config"></json>
                    <json :data="serverMap"></json>
                </v-col>
            </v-col>

        </v-row>
    </v-container>

</template>

<script>

import {mapState} from 'vuex';
import Json from 'vue-json-pretty';
import {serialize} from 'object-to-formdata';
import moment from 'moment';
import {timeDisplay} from '../../../components/mixins/timeDisplayMixin';
import TitleCard from "../../../components/UI/TitleCard";

const vuexModule = 'PanelistImporter';

export default {

    mixins: [timeDisplay],

    components: {
        TitleCard,
        Json,
    },

    props: {
        file: {
            type: File,
            required: true,
        },
    },

    data() {
        return {
            debug: false,
            serverMap: [],
            importing: false,

            updateKeys: [],
            updateKeyModel: null,

            updateRequiredFields: ['IDENTIFIER', 'EMAIL'],
            createRequiredFields: ['FIRST_NAME', 'LAST_NAME', 'EMAIL'],

            createRequiredFieldsMissing: [],

            createRecommendedFields: [
                {
                    name: 'RECRUITMENT_SOURCE',
                    found: false,
                    message: 'Value will be set to default recruitment source'
                },
                {name: 'LOCALE', found: false, message: 'Locale will be set to panel default locale'},
                {name: 'SUBSCRIBED_DATE', found: false, message: 'Subscribed date will be set to today\'s date'},
                {name: 'SUB_PANEL', found: false, message: 'Sub panel will be set to default'}
            ],
            createRecommendedFieldsMissing: [],

            datePickerModel: false,
            actionSwitchModel: false,

            config: {
                action: 'insert',
                subscribedDate: null,
                localeId: null,
                recruitmentSourceId: null,
                subPanelUuid: null,
                errorHandling: 'skip',
            },

            errorHandlingModes: [
                {
                    mode: 'abort',
                    name: 'Abort on any error'
                },
                {
                    mode: 'skip',
                    name: 'Skip and report lines with errors'
                }
            ],

        }
    },
    computed: {

        ...mapState('RecruitmentSources', [
            'recruitmentSources',
        ]),

        ...mapState('SubPanels', [
            'subPanels',
        ]),

        ...mapState('PanelSettings', [
            'settings',
        ]),

        ...mapState(vuexModule, [
            'mappings',
        ]),

        enabledRecruitmentSources() {
            return this.recruitmentSources.filter(source => source.enabled);
        },

        locales() {
            if (this.config.subPanelUuid) {

                const subPanel = this.subPanels.find(subPanel => subPanel.uuid === this.config.subPanelUuid);

                return this.settings.locales.filter(locale => {
                    return !!subPanel.locales.find(l => l.id === locale.id);
                })
            }
        },

        today() {
            return moment().format('YYYY-MM-DD');
        },

        createErrorsFound() {
            return this.createRequiredFieldsMissing.length > 0;
        },

        isUploadPossible() {

            if (this.createErrorsFound) return false;
            if (this.recruitmentSourceMissing && !this.config.recruitmentSourceId) return false;
            if (this.localeMissing && !this.config.localeId) return false;
            if (this.joinDateMissing && !this.config.subscribedDate) return false;
            if (this.subPanelMissing && !this.config.subPanelUuid) return false;

            return true;
        },

        updateKeysFound() {
            return this.updateKeys.length > 0;
        },

        recruitmentSourceMissing() {
            return !!this.createRecommendedFieldsMissing.find((field) => {
                return field.name === 'RECRUITMENT_SOURCE';
            });
        },

        localeMissing() {
            return !!this.createRecommendedFieldsMissing.find((field) => {
                return field.name === 'LOCALE';
            });
        },

        joinDateMissing() {
            return !!this.createRecommendedFieldsMissing.find((field) => {
                return field.name === 'SUBSCRIBED_DATE';
            });
        },

        subPanelMissing() {
            return !!this.createRecommendedFieldsMissing.find((field) => {
                return field.name === 'SUB_PANEL';
            });
        }
    },

    methods: {
        isVariableMapped(name) {
            let found = this.mappings.find(mapping => {
                return mapping.variable.name === name;
            });

            return found !== undefined;
        },

        backClicked() {
            this.$emit('back');
        },

        cancelClicked() {
            this.$emit('reset');
        },

        uploadFile() {
            // Deep copy
            this.serverMap = this.mappings.map(mapping => {
                return {
                    index: mapping.column.index,
                    dataVariableId: mapping.variable.id,
                    optionId: mapping.variable.optionId,
                    userActions: {
                        confirmed: mapping.column.userActions.confirmed ? 1 : 0,
                        skipValueErrors: mapping.column.userActions.skipValueErrors ? 1 : 0,
                        ignore: mapping.column.userActions.ignore ? 1 : 0
                    },
                }
            });

            // Convert date to utc
            this.config.subscribedDate = this.toUTCDate(this.config.subscribedDate);

            const data = {
                mappings: this.serverMap,
                config: this.config,
                file: this.file
            };

            this.importing = true;

            let formData = serialize(data, {indices: true});

            axios.post('/panelists/import', formData)
                .then((response) => {

                    Bus.$emit('flash-message', {
                        type: 'info',
                        message: 'File was successfully uploaded!',
                        duration: 2000
                    });
                })
                .catch((error) => {

                    if (error.response.status === 422) {
                        Bus.$emit('flash-message', {type: 'error', message: error.response.data.message});
                    } else {
                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: 'We are sorry something went wrong! Please try again.'
                        });
                    }
                })
                .finally(() => {
                    this.importing = false;
                    this.$emit('complete');
                });
        },
    },

    mounted() {

        this.$store.dispatch('RecruitmentSources/loadRecruitmentSources')
            .then((data) => {
                this.config.recruitmentSourceId = this.recruitmentSources[0].id;
            });

        this.$store.dispatch('SubPanels/loadSubPanels')
            .then((data) => {
                this.config.subPanelUuid = this.subPanels[0].uuid;
            });

        this.config.localeId = this.settings.locales[0].id;

        this.config.subscribedDate = moment().format('YYYY-MM-DD');

        this.createRequiredFieldsMissing = this.createRequiredFields.filter(field => {
            return !this.isVariableMapped(field);
        });

        this.createRecommendedFieldsMissing = this.createRecommendedFields.filter(field => {
            return !this.isVariableMapped(field.name);
        });
    },
}

</script>
